import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowsRotate,
    faCompress,
    faFloppyDisk,
    faMagnifyingGlassMinus,
    faMagnifyingGlassPlus,
} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {CalendarEmployeeDto, PlannerViewDto} from 'apiClient/Api';

import {CalendarEventDetail} from './calendarSources';
import {apiErrorMessage} from './errorText';
import EventDetailDialog from './EventDetailDialog';
import PlannerBoard, {NAME_COLUMN} from './PlannerBoard';
import {
    CATEGORY_COLOR,
    DAY_END_HOUR,
    DAY_START_HOUR,
    MAX_PIXELS_PER_HOUR,
    MIN_PIXELS_PER_HOUR,
    PIXELS_PER_HOUR,
    PlannerCategory,
    PlannerRange,
    ZOOM_MAX,
    ZOOM_MIN,
    ZOOM_STEP,
    plannerDays,
    toPlannerRows,
} from './plannerModel';

/** Die wählbaren Zoomstufen, von fein nach grob. */
const ZOOM_LEVELS: {value: PlannerRange; labelKey: string}[] = [
    {value: 'day', labelKey: 'planner.rangeDay'},
    {value: 'week', labelKey: 'planner.rangeWeek'},
    {value: 'twoWeeks', labelKey: 'planner.rangeTwoWeeks'},
    {value: 'month', labelKey: 'planner.rangeMonth'},
];

/**
 * Abstand der automatischen Aktualisierung, solange heute im Blick und die Seite sichtbar ist.
 *
 * Fünf Minuten sind der Ausgleich zwischen zwei Fehlern: ein alter Teams-Punkt behauptet etwas
 * Falsches, ein zu enger Takt belastet LabelConnect und Microsoft für eine Nebeninformation.
 */
const PRESENCE_REFRESH_MS = 5 * 60_000;

/** Schlüssel der gespeicherten Ansicht — muss zu `UserSettingKeys` im Server passen. */
const SETTING_PEOPLE = 'Planner.People';
const SETTING_RANGE = 'Planner.Range';

/** Die Legende — ohne sie sind sechs Farben Rätselraten. */
const LEGEND: {category: PlannerCategory; labelKey: string}[] = [
    {category: 'order', labelKey: 'calendar.sources.order'},
    {category: 'appointment', labelKey: 'calendar.sources.appointment'},
    {category: 'absence', labelKey: 'calendar.sources.absence'},
    {category: 'project', labelKey: 'calendar.sources.project'},
    {category: 'task', labelKey: 'calendar.sources.task'},
    {category: 'outlook', labelKey: 'calendar.sources.o365'},
];

/**
 * Der Planer: eine Planungstafel über mehrere Personen.
 *
 * Eine Zeile je Person, waagerechte Zeitachse. Ganztägiges (Urlaub, Krank) liegt in einem eigenen
 * Band über den Terminen mit Uhrzeit; überlappende Termine stapeln sich, aber nur wenn sie sich
 * tatsächlich überschneiden — eine ruhige Zeile bleibt flach.
 *
 * Die Personenauswahl wird als persönliche Einstellung gespeichert (`Planner.People`) — wer mit
 * denselben Kollegen plant, soll sie nicht jeden Morgen neu zusammenklicken.
 */
export const Planner: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('planner.title');
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [employees, setEmployees] = useState<CalendarEmployeeDto[]>([]);
    const [people, setPeople] = useState<CalendarEmployeeDto[]>([]);
    const [range, setRange] = useState<PlannerRange>('week');
    const [anchor, setAnchor] = useState<Dayjs>(() => dayjs().startOf('day'));
    /** Samstag und Sonntag fraßen ein Drittel der Breite für Spalten, in denen nie etwas steht. */
    const [showWeekends, setShowWeekends] = useState(false);
    /**
     * Stufenloser Zoom als Vielfaches der eingepassten Breite. 1 heißt „füllt das Fenster".
     *
     * Die vier Stufen daneben wählen den **Zeitraum**, der Zoom die **Auflösung** darin. Beides
     * getrennt zu halten ist der Unterschied zwischen vier festen Ansichten und einer Tafel, an der
     * man arbeiten kann: man will einen Vormittag genauer ansehen, ohne die Woche zu verlassen.
     */
    const [zoom, setZoom] = useState(1);

    /*
     * Die verfügbare Breite wird gemessen, damit eine Stunde mindestens ihre Zoombreite bekommt,
     * bei Platz aber aufgedehnt wird. Ohne das steht die Tafel entweder mit einer Lücke rechts
     * oder scrollt, obwohl alles hineinpassen würde.
     */
    const [boardWidth, setBoardWidth] = useState(0);
    const boardObserver = useRef<ResizeObserver | null>(null);

    /*
     * Bewusst eine Callback-Referenz statt `useRef` + `useEffect([])`: die Tafel steht erst im DOM,
     * wenn Zeilen da sind — vorher zeigt die Seite „wird geladen". Ein Effekt, der einmal beim
     * Einhängen läuft, findet dort nichts, hängt sich nie an und lässt die gemessene Breite für
     * immer auf 0 stehen. Die Folge war unsichtbar, aber dauerhaft: die Stundenbreite fiel stets
     * auf den festen Mindestwert der Zoomstufe zurück, und die Tagesansicht ließ rechts Platz
     * liegen. Die Callback-Referenz greift genau dann, wenn das Element kommt und geht.
     */
    const boardRef = useCallback((element: HTMLDivElement | null) => {
        boardObserver.current?.disconnect();
        boardObserver.current = null;
        if (!element) return;

        // Sofort messen: sonst zeigt das erste Bild die Mindestbreite und springt danach.
        setBoardWidth(element.clientWidth);

        const observer = new ResizeObserver(([entry]) => setBoardWidth(entry.contentRect.width));
        observer.observe(element);
        boardObserver.current = observer;
    }, []);

    useEffect(() => () => boardObserver.current?.disconnect(), []);

    const [view, setView] = useState<PlannerViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    /*
     * Zum ausgewählten Termin gehört, **wessen** Zeile er stand: der Knopf „In Outlook öffnen"
     * führt in das Postfach des Besitzers und ist für fremde Termine wertlos.
     */
    const [selected, setSelected] =
        useState<{detail: CalendarEventDetail; ownMailbox: boolean} | null>(null);

    /*
     * Eigener Uhrzeit-Zustand: der Jetzt-Strahl muss wandern, ohne dass jemand neu lädt — auf einer
     * Planungstafel steht man sonst irgendwann vor einer Linie, die eine halbe Stunde alt ist.
     */
    const [now, setNow] = useState<Dayjs>(() => dayjs());
    useEffect(() => {
        const timer = setInterval(() => setNow(dayjs()), 60_000);
        return () => clearInterval(timer);
    }, []);

    const days = useMemo(
        () => plannerDays(anchor, range, showWeekends),
        [anchor, range, showWeekends]);

    /**
     * Stundenbreite und die Untergrenze des Zooms.
     *
     * Herausgezoomt wird nur so weit, bis der Zeitraum das Fenster genau ausfüllt. Darunter
     * entstünde rechts leere Fläche, ohne dass mehr zu sehen wäre — in der Tagesansicht passt der
     * Tag ohnehin schon hinein, und dort hört das Herauszoomen deshalb sofort auf. In der Woche
     * greift es dagegen genau bis zu dem Punkt, an dem alle fünf Tage nebeneinanderstehen.
     */
    const {pixelsPerHour, minZoom} = useMemo(() => {
        const hours = days.length * (DAY_END_HOUR - DAY_START_HOUR);
        // Ein Pixel Luft: die Stundenbreite ist gebrochen, und die Summe darf nach dem Runden nicht
        // über die Breite hinauslaufen — sonst erscheint eine Bildlaufleiste für nichts.
        const available = Math.max(boardWidth - NAME_COLUMN - 1, 0);
        const filling = available / Math.max(hours, 1);
        const fitted = Math.max(PIXELS_PER_HOUR[range], filling);
        const floor = Math.max(filling, MIN_PIXELS_PER_HOUR);

        return {
            pixelsPerHour: Math.min(Math.max(fitted * zoom, floor), MAX_PIXELS_PER_HOUR),
            minZoom: fitted > 0 ? Math.max(ZOOM_MIN, floor / fitted) : ZOOM_MIN,
        };
    }, [days.length, range, boardWidth, zoom]);

    /** Einen Schritt hinein oder heraus — aus dem Knopf wie aus dem Mausrad. */
    const applyZoom = useCallback((direction: 1 | -1) => setZoom((current) => {
        const next = direction === 1 ? current * ZOOM_STEP : current / ZOOM_STEP;
        return Math.min(Math.max(next, minZoom), ZOOM_MAX);
    }), [minZoom]);

    /** Zeitraum eines Termins als Text — dieselbe Form wie im Detail-Dialog. */
    const formatPeriod = useCallback(
        (start?: string | null, end?: string | null, allDay?: boolean): string => {
            if (!start) return '—';
            const from = dayjs(start.replace(/(Z|[+-]\d{2}:\d{2})$/, ''));
            const day = from.toDate().toLocaleDateString(i18n.language,
                {weekday: 'short', day: '2-digit', month: '2-digit'});
            if (allDay) return `${day} · ${t('calendar.allDay')}`;
            const to = end ? dayjs(end.replace(/(Z|[+-]\d{2}:\d{2})$/, '')) : null;
            return to ? `${day} ${from.format('HH:mm')}–${to.format('HH:mm')}` : `${day} ${from.format('HH:mm')}`;
        }, [i18n.language, t]);

    const rows = useMemo(
        () => toPlannerRows(view, days, pixelsPerHour, t, formatPeriod),
        [view, days, pixelsPerHour, t, formatPeriod]);

    /**
     * Die Kennung der angemeldeten Person — ihre Zeile wird hervorgehoben. Die Anmeldedaten führen
     * nur die Konto-Mail; steht in Labelwin die Adresse der zweiten Hausdomain, greift die
     * Hervorhebung nicht. Das ist der harmlose Fehlerfall: eine Zeile ohne Marke statt einer
     * falschen Marke an fremder Zeile.
     */
    const selfIdentifiers = useMemo(
        () => [context.userData?.eMail].filter((value): value is string => !!value),
        [context.userData?.eMail]);

    // Mitarbeiterliste und gespeicherte Ansicht laden.
    useEffect(() => {
        let list: CalendarEmployeeDto[] = [];

        context.api.labelConnect
            .labelConnectGetEmployees()
            .then((response) => {
                list = response.data;
                setEmployees(list);
                return context.api.user.userGetMySetting(SETTING_PEOPLE);
            })
            .then((response) => {
                const stored = parseStoredPeople(response.data.value);
                // Über die Mailadressen zurück auf die Mitarbeiter abbilden: gespeichert werden
                // Adressen, weil Personalnummern zwar stabil, aber für den Server nicht die
                // Adressierung sind.
                setPeople(stored
                    .map((mail) => list.find((e) => matchesMail(e, mail)))
                    .filter((e): e is CalendarEmployeeDto => e !== undefined));
                return context.api.user.userGetMySetting(SETTING_RANGE);
            })
            .then((response) => {
                const stored = response.data.value;
                if (stored && ZOOM_LEVELS.some((level) => level.value === stored)) {
                    setRange(stored as PlannerRange);
                }
            })
            // Still: ohne gespeicherte Ansicht fängt man mit einer leeren Auswahl an.
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const identifiers = useMemo(
        () => people.map(mailOf).filter((m): m is string => !!m),
        [people]);

    const load = useCallback(() => {
        if (identifiers.length === 0) {
            setView(undefined);
            return;
        }

        setLoading(true);
        setError(undefined);
        context.api.calendar
            .calendarGetPlanner({
                users: identifiers.join(','),
                von: days[0].format('YYYY-MM-DD'),
                bis: days[days.length - 1].format('YYYY-MM-DD'),
            })
            .then((response) => setView(response.data))
            .catch((e: any) => {
                setError(apiErrorMessage(e, t('planner.loadFailed')));
                setView(undefined);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifiers.join(','), days[0].valueOf(), days[days.length - 1].valueOf()]);

    useEffect(() => {
        load();
    }, [load]);

    /*
     * Der Teams-Status ist eine Aussage über **jetzt** — ein Punkt von vor einer Stunde behauptet
     * etwas, das er nicht mehr weiß. Deshalb lädt die Tafel nach, aber nur unter zwei Bedingungen:
     * heute ist im Blick (sonst gibt es keinen „jetzt"-Zustand zu zeigen) und die Seite ist
     * sichtbar. Ohne die zweite Bedingung fragte jeder vergessene Hintergrund-Tab tagelang zwei
     * Fremdsysteme ab.
     */
    useEffect(() => {
        const showsToday = days.some((day) => day.isSame(dayjs(), 'day'));
        if (!showsToday || identifiers.length === 0) return undefined;

        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, PRESENCE_REFRESH_MS);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, identifiers.length, days[0].valueOf(), days[days.length - 1].valueOf()]);

    const saveView = async (): Promise<void> => {
        setSaving(true);
        try {
            await context.api.user.userSetMySetting(SETTING_PEOPLE, {
                value: JSON.stringify(identifiers),
            });
            await context.api.user.userSetMySetting(SETTING_RANGE, {value: range});
            enqueueSnackbar(t('planner.saved'), {variant: 'success'});
        } catch (e) {
            context.apiError(e);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Um wie viel ein Klick auf ‹ oder › verschiebt. Ein Schritt entspricht dem, was man gerade
     * sieht — sonst blättert man aus dem Blick heraus oder tritt auf der Stelle.
     */
    const step = (direction: 1 | -1): Dayjs => {
        switch (range) {
            case 'day':
                return anchor.add(direction, 'day');
            case 'week':
                return anchor.add(direction, 'week');
            case 'twoWeeks':
                return anchor.add(direction * 2, 'week');
            default:
                return anchor.add(direction, 'month');
        }
    };

    const rangeLabel = range === 'day'
        ? days[0].toDate().toLocaleDateString(i18n.language,
            {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'})
        : range === 'month'
            ? anchor.toDate().toLocaleDateString(i18n.language, {month: 'long', year: 'numeric'})
            : `${days[0].format('DD.MM.')} – ${days[days.length - 1].format('DD.MM.YYYY')}`;


    /**
     * Position der Jetzt-Linie in Prozent, oder `null` wenn der heutige Tag nicht im Blick ist
     * oder die aktuelle Uhrzeit außerhalb des gezeigten Abschnitts liegt. Auf einer Planungstafel
     * ist „wo stehen wir gerade?" die erste Frage.
     */
    const nowPosition = useMemo(() => {
        const dayIndex = days.findIndex((d) => d.isSame(now, 'day'));
        if (dayIndex === -1) return null;

        const minuteOfDay = now.hour() * 60 + now.minute() - DAY_START_HOUR * 60;
        const perDay = (DAY_END_HOUR - DAY_START_HOUR) * 60;
        if (minuteOfDay < 0 || minuteOfDay > perDay) return null;

        return (dayIndex * perDay + minuteOfDay) * (pixelsPerHour / 60);
    }, [days, now, pixelsPerHour]);

    return (
        <Grid size={12}>
            <Box sx={{mb: 2.5}}>
                <Typography variant="h5" sx={{fontWeight: 600}}>{t('planner.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('planner.subtitle')}</Typography>
            </Box>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {/*
              * Alles in einer Karte: Bedienung, Tafel und Legende gehören zusammen. Drei gestapelte
              * Kästen lesen sich als drei Dinge.
              */}
            <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                <Stack direction={{xs: 'column', lg: 'row'}} spacing={2}
                       sx={{px: 2, py: 1.75, alignItems: {lg: 'center'}}}>
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={4}
                        sx={{flexGrow: 1, minWidth: 260}}
                        options={employees}
                        value={people}
                        onChange={(_, value) => setPeople(value)}
                        getOptionLabel={(option) => option.name ?? option.labelUser ?? '—'}
                        isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                        renderInput={(params) => (
                            <TextField {...params} label={t('planner.peopleLabel')}
                                       placeholder={people.length === 0 ? t('planner.peoplePlaceholder') : ''}/>
                        )}
                    />

                    <Stack direction="row" spacing={1.5}
                           sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                        <Typography variant="subtitle2" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>
                            {rangeLabel}
                        </Typography>
                        {loading && <CircularProgress size={16}/>}

                        {/* Blättern als zusammenhängende Gruppe statt dreier loser Knöpfe. */}
                        <ButtonGroup size="small" variant="outlined">
                            <Button onClick={() => setAnchor(step(-1))}
                                    aria-label={t('calendar.previous')}>
                                <FontAwesomeIcon icon={faAngleLeft}/>
                            </Button>
                            <Button onClick={() => setAnchor(dayjs().startOf('day'))}>
                                {t('calendar.today')}
                            </Button>
                            <Button onClick={() => setAnchor(step(1))}
                                    aria-label={t('calendar.next')}>
                                <FontAwesomeIcon icon={faAngleRight}/>
                            </Button>
                        </ButtonGroup>

                        {/*
                          * Der Zeitraumwechsel setzt den Zoom zurück: eine vierfache Vergrößerung
                          * der Woche bedeutet im Monat etwas völlig anderes, und man landete sonst
                          * in einer Ansicht, die man nicht gewählt hat.
                          */}
                        <ToggleButtonGroup size="small" exclusive value={range}
                                           onChange={(_, value: PlannerRange | null) => {
                                               if (!value) return;
                                               setRange(value);
                                               setZoom(1);
                                           }}>
                            {ZOOM_LEVELS.map((level) => (
                                <ToggleButton key={level.value} value={level.value}>
                                    {t(level.labelKey)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <ButtonGroup size="small" variant="outlined">
                            <Tooltip title={t('planner.zoomOut')}>
                                <span>
                                    <Button onClick={() => applyZoom(-1)} disabled={zoom <= minZoom * 1.001}
                                            aria-label={t('planner.zoomOut')}>
                                        <FontAwesomeIcon icon={faMagnifyingGlassMinus}/>
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('planner.zoomFit')}>
                                <Button onClick={() => setZoom(1)}
                                        startIcon={<FontAwesomeIcon icon={faCompress}/>}
                                        sx={{fontVariantNumeric: 'tabular-nums', minWidth: 84}}>
                                    {Math.round(zoom * 100)} %
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('planner.zoomIn')}>
                                <span>
                                    <Button onClick={() => applyZoom(1)} disabled={zoom >= ZOOM_MAX}
                                            aria-label={t('planner.zoomIn')}>
                                        <FontAwesomeIcon icon={faMagnifyingGlassPlus}/>
                                    </Button>
                                </span>
                            </Tooltip>
                        </ButtonGroup>

                        <Tooltip title={t('planner.toggleWeekends')}>
                            <ToggleButton size="small" value="weekends" selected={showWeekends}
                                          onChange={() => setShowWeekends(!showWeekends)}>
                                {t('planner.weekendsShort')}
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title={t('calendar.reload')}>
                            <span>
                                <IconButton size="small" onClick={load} disabled={loading}>
                                    <FontAwesomeIcon icon={faArrowsRotate}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('planner.saveView')}>
                            <span>
                                <IconButton size="small" onClick={saveView}
                                            disabled={saving || identifiers.length === 0}>
                                    <FontAwesomeIcon icon={faFloppyDisk}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Divider/>

                {identifiers.length === 0 ? (
                    <Box sx={{px: 3, py: 6, textAlign: 'center'}}>
                        <Typography variant="body2" color="text.secondary">{t('planner.empty')}</Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    /*
                      * Personen gewählt, aber keine Zeile: entweder läuft der Abruf noch oder die
                      * Antwort war leer. Beides gehört benannt — eine stumme Fläche sieht wie ein
                      * Fehler aus und sagt nicht, ob man warten soll.
                      */
                    <Box sx={{px: 3, py: 6, textAlign: 'center'}}>
                        <Typography variant="body2" color="text.secondary">
                            {loading ? t('planner.loading') : t('planner.noRows')}
                        </Typography>
                    </Box>
                ) : (
                    <Box ref={boardRef} sx={{maxHeight: 'calc(100vh - 330px)', overflowY: 'auto'}}>
                        <PlannerBoard rows={rows} days={days} pixelsPerHour={pixelsPerHour}
                                      nowPosition={nowPosition} nowLabel={now.format('HH:mm')}
                                      selfIdentifiers={selfIdentifiers}
                                      onSelect={(detail, ownMailbox) =>
                                          setSelected({detail, ownMailbox})}
                                      onZoom={applyZoom}/>
                    </Box>
                )}

                <Divider/>

                <Stack direction="row" spacing={2}
                       sx={{px: 2, py: 1.25, flexWrap: 'wrap', rowGap: 1, alignItems: 'center'}}>
                    {LEGEND.map(({category, labelKey}) => (
                        <Stack key={category} direction="row" spacing={0.75} sx={{alignItems: 'center'}}>
                            <Box sx={{
                                width: 10, height: 10, borderRadius: '2px',
                                bgcolor: CATEGORY_COLOR[category],
                            }}/>
                            <Typography variant="caption" color="text.secondary">{t(labelKey)}</Typography>
                        </Stack>
                    ))}
                    <Box sx={{flexGrow: 1}}/>
                    <Typography variant="caption" color="text.secondary">
                        {t('planner.legendFree')} · {t('planner.legendHours', {from: DAY_START_HOUR, to: DAY_END_HOUR})}
                        {' · '}{t('planner.zoomHint')}
                    </Typography>
                </Stack>
            </Paper>

            <EventDetailDialog detail={selected?.detail ?? null}
                               ownMailbox={selected?.ownMailbox ?? true}
                               onClose={() => setSelected(null)}/>
        </Grid>
    );
};

/** Die Adresse, unter der eine Person adressiert wird. */
const mailOf = (employee: CalendarEmployeeDto): string | undefined =>
    employee.mail || employee.mailInternal || employee.labelUser || undefined;

/** Passt die gespeicherte Adresse zu diesem Mitarbeiter? Beide Mail-Domains prüfen. */
const matchesMail = (employee: CalendarEmployeeDto, mail: string): boolean =>
    [employee.mail, employee.mailInternal, employee.labelUser]
        .some((value) => value && value.toLowerCase() === mail.toLowerCase());

/** Liest die gespeicherte Personenliste; unbrauchbarer Inhalt gilt als „nichts gespeichert". */
const parseStoredPeople = (value?: string | null): string[] => {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
    } catch {
        return [];
    }
};

export default Planner;
